/* eslint-disable no-unused-vars */
import React from 'react';
import { Avatar, Box, Flex, Icon, Image, Text, useDisclosure } from '@chakra-ui/react';
import { SignOut, User } from 'phosphor-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PrivateRoutes } from '../../routes/private.routes';
import logo from '../../assets/images/logo_black.png';
import { useAuth } from '../../providers/auth';
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '../ui/sidebar';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuChevron,
  SidebarMenuItem,
  SidebarMenuSub,
} from '../ui/sidebar-menu';

import packageJson from '../../../package.json';

const { version } = packageJson;

const menuGroups: { label: string | null; names: string[] }[] = [
  { label: null, names: ['Dashboard'] },
  {
    label: 'Pessoas',
    names: ['Tutores', 'Professores', 'Usuários', 'Secretarias', 'Instituições'],
  },
  {
    label: 'Ensino',
    names: ['Conteúdos', 'Materiais Extras', 'Templates', 'Avaliações', 'Tópicos'],
  },
  { label: 'Sistema', names: ['Notificações', 'Permissões', 'Configurações'] },
];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

const CollapsibleNavItem = ({
  link,
  submenus,
  hasActiveChild,
  isPathActive,
  onNavigate,
}: {
  link: any;
  submenus: any[];
  hasActiveChild: boolean;
  isPathActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: hasActiveChild });

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        icon={link.icon}
        tooltip={link.name}
        isActive={hasActiveChild && !isOpen}
        endElement={<SidebarMenuChevron isOpen={isOpen} />}
        data-testid={`sidebar-parent-${slugify(link.name)}`}
        onClick={onToggle}
      >
        {link.name}
      </SidebarMenuButton>
      <SidebarMenuSub isOpen={isOpen}>
        {submenus.map((submenu) => (
          <SidebarMenuItem key={submenu.name}>
            <SidebarMenuButton
              isActive={isPathActive(submenu.path as string)}
              data-testid={`sidebar-child-${slugify(submenu.name)}`}
              onClick={() => onNavigate(submenu.path as string)}
            >
              {submenu.name}
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenuSub>
    </SidebarMenuItem>
  );
};

const Sidebar = () => {
  const { Logout, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === 'collapsed' && !isMobile;

  const go = (path: string) => {
    navigate(path);
    if (isMobile) setOpenMobile(false);
  };

  const isPathActive = (path: string) => pathname === path || pathname.startsWith(`${path}/`);

  const routeSettings = [
    {
      groupId: 1,
      name: 'Sistema',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        '/students',
        '/secretaries',
        '/institutions/list',
        '/assessments/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/topics',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
        '/templates',
        '/templates/list',
        '/templates/link',
        '/modules',
        '/notifications',
        '/users',
        '/permissions',
        '/permissions/list',
        '/permissions/logs',
        '/config',
        '/settings/general',
        '/settings/terms',
        '/settings/politics',
      ],
      redirect: '/',
    },
    {
      groupId: 2,
      name: 'admin',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        '/students',
        '/secretaries',
        '/assessments/list',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/topics',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
        '/templates',
        '/templates/list',
        '/templates/link',
        '/modules',
        '/notifications',
        '/users',
        '/permissions',
        '/permissions/list',
        '/permissions/logs',
        '/config',
        '/settings/general',
        '/settings/terms',
        '/settings/politics',
      ],
      redirect: '/',
    },
    {
      groupId: 3,
      name: 'Tutor',
      routes: [
        '/',
        '/teachers',
        '/secretaries',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
      ],
      redirect: '/',
    },
    {
      groupId: 6,
      name: 'Responsável da secretaria',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        // WEB-06: Secretário não deve ver aba "Secretarias"
        // '/secretaries',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/extraMaterials',
        '/notifications',
      ],
      redirect: '/',
    },
    {
      groupId: 7,
      name: 'Responsável da instituição',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        '/studenst',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/user/institutions',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
      ],
      redirect: '/',
    },
  ];

  const findUser = routeSettings.find((item) => item.groupId === user.group.id);
  const topRoutes = PrivateRoutes.filter((item) => item.localeSidebar === 'top' && item.showSidebar)
    .filter((item) => findUser?.routes.find((routeByUser) => item?.path === routeByUser))
    .filter((item) => item.path !== '/profile');

  const childrenOf = (parentName: string) =>
    PrivateRoutes.filter((item) =>
      user.group.id === 6 ? item?.path !== '/contents/categories' : item.parentMenu === parentName
    )
      .filter((item) => item.parentMenu === parentName)
      .filter((item) =>
        user.group.id === 7 && item?.path === '/institutions/list'
          ? undefined
          : item.parentMenu === parentName
      );

  const renderLink = (link: any) => {
    if (!link.isParentMenu) {
      return (
        <SidebarMenuItem key={link.name}>
          <SidebarMenuButton
            icon={link.icon}
            tooltip={link.name}
            isActive={isPathActive(link.path as string)}
            data-testid={`sidebar-link-${slugify(link.name)}`}
            onClick={() => go(link.path as string)}
          >
            {link.name}
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }
    const submenus = childrenOf(link.name);
    const hasActiveChild = submenus.some((submenu) => isPathActive(submenu.path as string));
    return (
      <CollapsibleNavItem
        key={link.name}
        link={link}
        submenus={submenus}
        hasActiveChild={hasActiveChild}
        isPathActive={isPathActive}
        onNavigate={go}
      />
    );
  };

  const visibleRoutes = user.group.id === 7 || user.group.id < 7 ? topRoutes : [];
  const knownNames = new Set(menuGroups.flatMap((group) => group.names));
  const grouped = menuGroups
    .map((group) => ({
      ...group,
      links: group.names
        .map((name) => visibleRoutes.find((link) => link.name === name))
        .filter(Boolean) as any[],
    }))
    .filter((group) => group.links.length > 0);
  const leftovers = visibleRoutes.filter((link) => !knownNames.has(link.name));
  if (leftovers.length > 0) grouped.push({ label: 'Outros', names: [], links: leftovers });

  return (
    <SidebarRoot>
      <SidebarHeader>
        <Flex h="36px" align="center" gap="8px" justify={collapsed ? 'center' : 'space-between'}>
          {!collapsed && (
            <Box flex="1" minW={0} overflow="hidden">
              <Image src={logo} h="26px" objectFit="contain" alt="Jornada Ser" />
            </Box>
          )}
          <SidebarTrigger />
        </Flex>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {grouped.map((group, index) => (
          <SidebarGroup key={group.label ?? 'top'} pt={index === 0 ? '12px' : '20px'}>
            {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>{group.links.map((link: any) => renderLink(link))}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton icon={SignOut} tooltip="Sair" onClick={Logout}>
              Sair
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {!collapsed && (
          <Flex
            as="button"
            type="button"
            w="full"
            align="center"
            gap="10px"
            p="8px"
            borderRadius="lg"
            border="1px solid"
            borderColor="line.hairline"
            bg="surface.base"
            cursor="pointer"
            textAlign="left"
            _hover={{ bg: 'surface.selected' }}
            onClick={() => go('/profile')}
          >
            <Avatar
              size="sm"
              w="28px"
              h="28px"
              src={user.image?.original}
              icon={<Icon as={User} w="14px" h="14px" />}
            />
            <Box overflow="hidden">
              <Text fontSize="13px" lineHeight="18px" fontWeight={500} noOfLines={1}>
                {user.name}
              </Text>
              <Text fontSize="12px" lineHeight="16px" color="content.subtle" noOfLines={1}>
                {user.group?.name}
              </Text>
            </Box>
          </Flex>
        )}

        {!collapsed && (
          <Text fontFamily="mono" fontSize="12px" color="content.faint" px="8px">
            {`v${version}`}
          </Text>
        )}
      </SidebarFooter>
    </SidebarRoot>
  );
};

export default Sidebar;
